import { Gender, GenderLabel } from "@/constants";
import { useDisclosure } from "@/hooks";
import { useCreateNewRoom, useGetRooms } from "@/hooks/query-hooks/useRoom";
import { useNotificationStore } from "@/states/zustand/notification";
import {
	Button,
	Drawer,
	Form,
	Input,
	InputNumber,
	Segmented,
	Space,
} from "antd";
import { AiOutlineMinusCircle, AiOutlinePlus } from "react-icons/ai";

export function CreateNewRoom() {
	const [isOpen, { toggle }] = useDisclosure();
	const [form] = Form.useForm();
	const { mutate, isPending } = useCreateNewRoom();
	const { data: existingRooms = [] } = useGetRooms();

	const handleSubmit = (values: { rooms: CreateRoomRequest[] }) => {
		const rooms = values.rooms || [];
		if (rooms.length === 0) return;

		mutate(rooms, {
			onSuccess: (data, rooms) => {
				toggle();
				form.resetFields();
				const roomNames = rooms?.map((it) => it.name).join(", ");
				useNotificationStore.getState().setNotification({
					type: "success",
					message: `${roomNames} : total ${rooms?.length} created successfully!`,
				});
			},
		});
	};

	const handleClose = () => {
		form.resetFields();
		toggle();
	};

	return (
		<>
			<Button
				icon={<AiOutlinePlus />}
				key={"new-room"}
				size="large"
				type="primary"
				className="bg-primary-gradient"
				onClick={toggle}
				shape="circle"
			/>
			<Drawer
				title={
					<h2 className="text-xl font-semibold text-primary-gradient font-poppins">
						Create New Rooms
					</h2>
				}
				width={600}
				onClose={handleClose}
				open={isOpen}
				extra={
					<Space>
						<Button
							onClick={handleClose}
							disabled={isPending}
							shape="round"
							size="large"
						>
							Cancel
						</Button>
						<Button
							type="primary"
							onClick={() => form.submit()}
							loading={isPending}
							shape="round"
							size="large"
							className="bg-primary-gradient"
						>
							Create
						</Button>
					</Space>
				}
				closeIcon={null}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{
						rooms: [
							{ name: "", maxiCount: 1, gender: Gender.MONK },
						],
					}}
				>
					<Form.List name="rooms">
						{(fields, { add, remove }) => (
							<>
								{fields.map(
									({ key, name, ...restField }, index) => (
										<div
											key={key}
											className="p-5 border border-gray-200 rounded-lg mb-4 relative bg-gray-50/50"
										>
											<article className="flex justify-between items-center mb-4">
												<h3 className="font-semibold text-gray-700">
													Room {index + 1}
												</h3>
												{fields.length > 1 && (
													<AiOutlineMinusCircle
														className="text-red-500 cursor-pointer text-xl hover:text-red-600 transition-colors"
														onClick={() =>
															remove(name)
														}
														title="Remove room"
													/>
												)}
											</article>
											<div className="grid grid-cols-3 gap-6">
												<Form.Item
													{...restField}
													label="အခန်း နာမည်"
													name={[name, "name"]}
													rules={[
														{
															required: true,
															message:
																"Please enter room name",
														},
														({
															getFieldValue,
														}) => ({
															validator(
																_,
																value,
															) {
																if (!value)
																	return Promise.resolve();
																const normalizedValue =
																	value
																		.toLowerCase()
																		.trim();

																// Check if it already exists in the backend
																const isDbDuplicate =
																	existingRooms.some(
																		(
																			r: Room,
																		) =>
																			r.name
																				.toLowerCase()
																				.trim() ===
																			normalizedValue,
																	);
																if (
																	isDbDuplicate
																) {
																	return Promise.reject(
																		new Error(
																			"Room name already exists in system",
																		),
																	);
																}

																// Check for duplicates within this dynamic form
																const currentRooms =
																	getFieldValue(
																		"rooms",
																	) || [];
																const duplicateCount =
																	currentRooms.filter(
																		(
																			r: Room,
																		) =>
																			r &&
																			r.name &&
																			r.name
																				.toLowerCase()
																				.trim() ===
																				normalizedValue,
																	).length;

																if (
																	duplicateCount >
																	1
																) {
																	return Promise.reject(
																		new Error(
																			"Duplicate name in this form!",
																		),
																	);
																}

																return Promise.resolve();
															},
														}),
													]}
													rootClassName="col-span-2"
												>
													<Input
														placeholder="eg: အခန်း ၁"
														size="large"
													/>
												</Form.Item>
												<Form.Item
													{...restField}
													label="ယောဂီ အရေအတွက်"
													name={[name, "maxiCount"]}
													rules={[
														{
															required: true,
															message:
																"Please enter max count",
														},
													]}
												>
													<InputNumber
														min={1}
														style={{
															width: "100%",
														}}
														size="large"
													/>
												</Form.Item>
											</div>
											<Form.Item
												{...restField}
												label="Gender"
												name={[name, "gender"]}
												rules={[
													{
														required: true,
														message:
															"Please select gender",
													},
												]}
												className="mb-0!"
											>
												<Segmented
													options={Object.values(Gender)
														.filter((value) => value !== Gender.UNKNOWN)
														.map((value) => ({
															label: GenderLabel[value as Gender],
															value: value,
														}))}
													block
													size="large"
												/>
											</Form.Item>
										</div>
									),
								)}
								<Form.Item>
									<Button
										type="dashed"
										onClick={() =>
											add({
												name: "",
												maxiCount: 1,
												gender: Gender.MONK,
											})
										}
										block
										icon={<AiOutlinePlus />}
										size="large"
										className="h-12 border-[#155DFC] text-[#155DFC] hover:text-[#155DFC] hover:border-[#155DFC]"
									>
										Add Another Room
									</Button>
								</Form.Item>
							</>
						)}
					</Form.List>
				</Form>
			</Drawer>
		</>
	);
}
